import { Box, ButtonBase, Checkbox, InputBase, styled } from "@mui/material";

import {
    COLORS,
    RADIUS,
    SHADOW,
    TYPOGRAPHY,
} from "../../SignalWizard.constants";
import { transientOptions } from "../../SignalWizard.styled";

export const Controls = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 24,
    [theme.breakpoints.up("sm")]: {
        flexDirection: "row",
        alignItems: "center",
    },
}));

export const SearchWrap = styled(Box)({
    position: "relative",
    flex: 1,
    maxWidth: 448,
});

export const FilterButton = styled(ButtonBase)({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    backgroundColor: COLORS.surfaceLowest,
    color: COLORS.onSurface,
    borderRadius: RADIUS.control,
    border: `1px solid ${COLORS.border}`,
    boxShadow: SHADOW.sm,
    transition: "background-color 150ms",
    whiteSpace: "nowrap",
    ...TYPOGRAPHY.labelMd,
    "&:hover": { backgroundColor: COLORS.surfaceLow },
    "& .filter-label": {
        color: COLORS.onSurfaceVariant,
        fontWeight: 400,
    },
    "& svg": { fontSize: 16, color: COLORS.outline },
});

export const FilterOptionIcon = styled("img")({
    width: 18,
    height: 18,
    marginRight: 10,
    objectFit: "contain",
});

export const FilterOptionCount = styled("span")({
    marginLeft: "auto",
    paddingLeft: 16,
    color: COLORS.outline,
    ...TYPOGRAPHY.bodySm,
});

export const SearchInput = styled(InputBase)({
    width: "100%",
    padding: "0 12px",
    gap: 8,
    backgroundColor: COLORS.surfaceLowest,
    color: COLORS.onSurface,
    borderRadius: RADIUS.control,
    border: `1px solid ${COLORS.border}`,
    boxShadow: SHADOW.sm,
    transition: "border-color 150ms, box-shadow 150ms",
    ...TYPOGRAPHY.bodyMd,
    "& svg": { fontSize: 17, color: COLORS.outline },
    "& input": {
        padding: "8px 0",
        height: "auto",
        "&::placeholder": { color: COLORS.outlineVariant, opacity: 1 },
    },
    "&.Mui-focused": {
        borderColor: COLORS.primary,
        boxShadow: "0 0 0 2px rgba(0, 0, 0, 0.05)",
    },
});

export const ListBody = styled(Box)({
    "& > *:not(:last-child)": {
        borderBottom: `1px solid ${COLORS.borderSubtle}`,
    },
});

export const Row = styled(
    Box,
    transientOptions,
)<{ $disabled?: boolean }>(({ $disabled }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: 16,
    cursor: $disabled ? "not-allowed" : "pointer",
    opacity: $disabled ? 0.55 : 1,
    transition: "background-color 150ms",
    "&:hover": {
        backgroundColor: $disabled ? "transparent" : "rgba(243, 243, 245, 0.4)",
    },
}));

export const RowMain = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: 14,
    flex: 1,
    minWidth: 0,
});

export const RowCheckbox = styled(Checkbox)({
    padding: 0,
    color: COLORS.outlineVariant,
    "&.Mui-checked": { color: COLORS.primary },
    "& .MuiSvgIcon-root": { fontSize: 20 },
});

export const IconTile = styled(
    Box,
    transientOptions,
)<{ $size?: number }>(({ $size = 36 }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: $size,
    height: $size,
    borderRadius: RADIUS.chip,
    backgroundColor: COLORS.surfaceLow,
    border: `1px solid rgba(198, 198, 205, 0.3)`,
    "& img": {
        width: Math.round($size * 0.6),
        height: Math.round($size * 0.6),
        objectFit: "contain",
    },
}));

export const ItemInfo = styled(Box)({
    flex: 1,
    minWidth: 0,
});

export const ItemTitleRow = styled(Box)({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
});

export const ItemName = styled("span")({
    color: COLORS.primary,
    ...TYPOGRAPHY.headlineSm,
    wordBreak: "break-word",
});

export const ItemMeta = styled("p")({
    margin: "2px 0 0",
    color: COLORS.onSurfaceVariant,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ...TYPOGRAPHY.bodySm,
});

export const RowActions = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexShrink: 0,
    "& .MuiIconButton-root": {
        padding: 4,
        borderRadius: RADIUS.control,
        color: COLORS.onSurfaceVariant,
        "&:hover": {
            color: COLORS.primary,
            backgroundColor: COLORS.surfaceHigh,
        },
    },
});

export const EmptyState = styled(Box)({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    padding: "40px 16px",
    textAlign: "center",
    color: COLORS.onSurfaceVariant,
    ...TYPOGRAPHY.bodyMd,
});

export const EmptyTitle = styled("span")({
    color: COLORS.primary,
    ...TYPOGRAPHY.headlineSm,
});

export const LoaderWrap = styled(Box)({
    display: "flex",
    justifyContent: "center",
    padding: "40px 0",
});
